// First-draft content — grounded in the AdsTxtChecker.jsx UI (3 input modes,
// errors-vs-warnings split, Duplicates card, freeform result search).
// Cross-check against adsTxt.js / adsTxtUtils.js when those land.

export default {
  title: 'Ads.txt / App-ads.txt Checker',
  summary:
    'Validate ads.txt and app-ads.txt files for format compliance, duplicate lines, and authorization integrity — by domain, by app bundle, or by pasting raw content.',

  sections: [
    {
      h: 'What this tool does',
      p: [
        "Pulls a publisher's `ads.txt` (or `app-ads.txt`) and checks every line against IAB format rules, flags duplicate authorizations, and verifies seller entries against the partner's **live sellers.json** rather than trusting the declared TAG-ID at face value.",
        "Hard errors and softer warnings are surfaced separately, so a malformed line and a line that simply doesn't resolve in sellers.json don't end up in the same pile.",
      ],
    },
    {
      h: 'When to use it',
      list: [
        'Onboarding a publisher and you need to confirm your exchange/SSP lines are present and well-formed before you switch on spend.',
        'A buyer is questioning authorization on a domain and you want the receipts.',
        "You're auditing app inventory and all you have is a bundle ID or a store URL — no domain.",
      ],
    },
    {
      h: 'How to use it',
      steps: [
        '**Pick your input.** A domain (`publisher.com` or `app.example.com`) fetches the live file. For apps, paste a bundle ID, a numeric iTunes ID, or a full store URL and the tool resolves the developer domain for you. To check a file that isn\u2019t published yet, paste the raw content.',
        "**Run the check.** You'll get a record count and a summary breakdown of valid lines, duplicates, and warnings.",
        '**Read errors and warnings separately.** Errors break the format. Warnings \u2014 e.g. a seller line that doesn\u2019t resolve in sellers.json \u2014 are worth a look but won\u2019t always need action.',
        '**Use the Duplicates card** to catch the same authorization declared twice. It\u2019s the usual residue of manual edits.',
        '**Search within results** by domain, publisher ID, TAG-ID, or full line to confirm one specific partner is authorized.',
      ],
    },
    {
      callout:
        '`app-ads.txt` vs `ads.txt` is resolved automatically from the input type \u2014 you don\u2019t pick the file. If a bundle resolves to the wrong developer domain, the problem is upstream in the store listing, not in the file.',
    },
    {
      h: 'Input modes',
      table: {
        head: ['Input', 'Example', 'What it checks'],
        rows: [
          ['Domain', 'publisher.com', 'Live ads.txt'],
          ['App bundle / store URL', 'com.example.app \u00b7 1473573813', 'Resolves developer domain \u2192 app-ads.txt'],
          ['Raw paste', '(file contents)', 'Format only \u2014 no live fetch'],
        ],
      },
    },
  ],

  faqs: [
    {
      q: 'Why does a line show as a warning when the format looks fine?',
      a: "Format can be perfectly valid while the seller entry doesn't resolve in the partner's sellers.json. That mismatch is what the warning flags — it's an authorization question, not a syntax one.",
    },
    {
      q: 'It says duplicates but I only see one line.',
      a: 'Duplicates are matched on the seller fields, not the whole line. A trailing comment or a whitespace difference still counts as the same authorization.',
    },
    {
      q: 'The bundle resolved to the wrong app.',
      a: "The resolver reads the store listing's developer domain. If the listing is wrong or the app changed hands, fix it at the store — the checker only reports what's published.",
    },
  ],
};
